/**
 * Cheesy Store: queso giratorio y control global de movimiento.
 *
 * - <chz-symbol> se pausa cuando sale de pantalla (un solo IntersectionObserver para todas las copias).
 * - Los botones [data-chz-motion-toggle] pausan o reanudan todas las animaciones continuas.
 *   Por defecto todo se mueve. La pausa vive en localStorage ('chz-motion-v2') y se aplica como
 *   html[data-chz-motion='off'] (layout/theme.liquid la aplica antes de pintar). La clave pasó a v2 el
 *   15-oct-2026 para que se olviden las pausas guardadas antes y todos partan con las animaciones.
 * El módulo se ejecuta una vez por página; los elementos se registran solos al conectarse,
 * así que funciona igual cuando el editor vuelve a renderizar una sección.
 */

const STORAGE_KEY = 'chz-motion-v2';
const OLD_STORAGE_KEY = 'chz-motion';

/** @type {IntersectionObserver | null} */
let observer = null;

function getObserver() {
  if (observer || !('IntersectionObserver' in window)) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle('is-paused', !entry.isIntersecting);
      }
    },
    { rootMargin: '64px' }
  );
  return observer;
}

class ChzSymbol extends HTMLElement {
  connectedCallback() {
    getObserver()?.observe(this);
  }

  disconnectedCallback() {
    observer?.unobserve(this);
  }
}

if (!customElements.get('chz-symbol')) {
  customElements.define('chz-symbol', ChzSymbol);
}

function readPreference() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePreference(value) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* Sin almacenamiento: la preferencia dura hasta recargar. */
  }
}

function applyMotion(isOn) {
  const root = document.documentElement;
  if (isOn) delete root.dataset.chzMotion;
  else root.dataset.chzMotion = 'off';
  for (const button of document.querySelectorAll('[data-chz-motion-toggle]')) {
    button.dataset.state = isOn ? 'on' : 'off';
  }
}

if (!window.__chzMotionInit) {
  window.__chzMotionInit = true;
  try {
    localStorage.removeItem(OLD_STORAGE_KEY);
  } catch {
    /* sin almacenamiento */
  }
  applyMotion(readPreference() !== 'off');

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-chz-motion-toggle]') : null;
    if (!target) return;
    const isOn = document.documentElement.dataset.chzMotion !== 'off';
    applyMotion(!isOn);
    writePreference(isOn ? 'off' : null);
  });

  // Botones nuevos que llegan con una sección re-renderizada en el editor.
  document.addEventListener('shopify:section:load', () => {
    applyMotion(document.documentElement.dataset.chzMotion !== 'off');
  });
}

/* Lo que trae data-chz-until (fin del sorteo, en segundos) se oculta cuando vence, aunque la página venga de caché. */
function hideExpired() {
  const now = Date.now() / 1000;
  for (const element of document.querySelectorAll('[data-chz-until]')) {
    const until = Number(element.getAttribute('data-chz-until'));
    if (until > 0 && now > until && element instanceof HTMLElement) element.hidden = true;
  }
}

if (!window.__chzUntilInit) {
  window.__chzUntilInit = true;
  hideExpired();
  document.addEventListener('shopify:section:load', hideExpired);
  window.addEventListener('pageshow', hideExpired);
}
